import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Save, Download, Upload, Trash2, RefreshCcw, ShieldAlert,
  Clock, Package, Loader2, HardDrive, PlayCircle, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function BackupTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cfg, setCfg] = useState({ enabled: false, hour: 3, keep: 14 });
  const [savingCfg, setSavingCfg] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: c }] = await Promise.all([
        api.get("/backup/list"),
        api.get("/backup/settings"),
      ]);
      setList(l || []);
      setCfg({
        enabled: !!c.enabled,
        hour: c.hour ?? 3,
        keep: c.keep ?? 14,
        last_auto_at: c.last_auto_at,
        last_auto_filename: c.last_auto_filename,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setCreating(true);
    const t = toast.loading("Yedek oluşturuluyor...");
    try {
      const { data } = await api.post("/backup/create");
      toast.dismiss(t);
      toast.success(`Yedek oluşturuldu: ${data.filename} (${data.size_human})`);
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiError(err));
    } finally { setCreating(false); }
  };

  const downloadBackup = (filename) => {
    // Force full URL + credentials via cookie
    const url = `${API}/backup/download/${encodeURIComponent(filename)}`;
    // fetch then blob to keep auth cookies
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success(`İndirildi: ${filename}`);
      })
      .catch((err) => toast.error(`İndirme başarısız: ${err.message}`));
  };

  const doRestore = async (filename) => {
    setRestoring(true);
    const t = toast.loading(`'${filename}' geri yükleniyor... (30-60 sn)`);
    try {
      const { data } = await api.post(`/backup/restore/${encodeURIComponent(filename)}`);
      toast.dismiss(t);
      toast.success(data.message);
      setConfirmRestore(null);
      // Reload the page so all fresh data loads
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiError(err));
    } finally { setRestoring(false); }
  };

  const doDelete = async (filename) => {
    try {
      await api.delete(`/backup/${encodeURIComponent(filename)}`);
      toast.success(`Silindi: ${filename}`);
      setConfirmDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".tar.gz")) {
      toast.error("Sadece .tar.gz uzantılı yedek dosyaları desteklenir");
      return;
    }
    setUploading(true);
    const t = toast.loading(`Yükleniyor & geri yükleniyor: ${file.name}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/upload-restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(t);
      toast.success(data.message);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      await api.post("/backup/settings", {
        enabled: !!next.enabled, hour: next.hour, keep: next.keep,
      });
      toast.success("Otomatik yedekleme ayarları kaydedildi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingCfg(false); }
  };

  const totalSize = list.reduce((s, b) => s + (b.size_bytes || 0), 0);
  const totalHuman = totalSize < 1024 ? `${totalSize} B`
    : totalSize < 1024 * 1024 ? `${(totalSize / 1024).toFixed(1)} KB`
    : totalSize < 1024 ** 3 ? `${(totalSize / 1024 ** 2).toFixed(1)} MB`
    : `${(totalSize / 1024 ** 3).toFixed(1)} GB`;

  return (
    <div className="space-y-5">
      {/* İşlem Kartı */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
              Manuel İşlemler
            </div>
            <h3 className="text-lg font-semibold text-slate-100">Yedekleme & Geri Yükleme</h3>
            <p className="text-xs text-slate-400 mt-1">
              Tüm veritabanının anlık görüntüsünü alır; sıkıştırılmış .gz arşivi olarak saklanır.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="backup-create-btn"
              onClick={createBackup}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Şimdi Yedekle
            </Button>
            <Button
              data-testid="backup-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Yedekten Yükle
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,application/gzip"
              onChange={handleUpload}
              className="hidden"
              data-testid="backup-upload-input"
            />
            <Button
              onClick={load}
              variant="ghost"
              className="text-slate-400 hover:bg-slate-800 rounded-sm"
              title="Listeyi yenile"
            >
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Otomatik Yedekleme Ayarları */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
            Otomatik Yedekleme
          </div>
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" /> Günlük Otomatik Yedek
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Aktif</Label>
            <div className="flex items-center gap-3 h-9">
              <Switch
                data-testid="backup-auto-toggle"
                checked={cfg.enabled}
                onCheckedChange={(v) => saveCfg({ enabled: v })}
                disabled={savingCfg}
              />
              <span className="text-sm text-slate-300">{cfg.enabled ? "Açık" : "Kapalı"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Saat (24s, sunucu UTC)
            </Label>
            <Select
              value={String(cfg.hour)}
              onValueChange={(v) => saveCfg({ hour: Number(v) })}
            >
              <SelectTrigger
                data-testid="backup-hour-select"
                className="bg-slate-950 border-slate-700 rounded-sm font-mono"
                disabled={!cfg.enabled || savingCfg}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 rounded-sm max-h-72">
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {String(h).padStart(2, "0")}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Kaç Yedek Saklansın?
            </Label>
            <Input
              data-testid="backup-keep-input"
              type="number"
              min={1}
              max={90}
              value={cfg.keep}
              onChange={(e) => setCfg({ ...cfg, keep: Number(e.target.value) })}
              onBlur={() => saveCfg({ keep: cfg.keep })}
              disabled={!cfg.enabled || savingCfg}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
        </div>
        {cfg.last_auto_at && (
          <div className="text-xs text-slate-500 font-mono border-t border-slate-800 pt-3">
            Son otomatik yedek: <span className="text-slate-300">{new Date(cfg.last_auto_at).toLocaleString("tr-TR")}</span>
            {cfg.last_auto_filename && <span className="ml-2">({cfg.last_auto_filename})</span>}
          </div>
        )}
      </Card>

      {/* Yedek Listesi */}
      <Card className="rounded-sm border-slate-800 bg-slate-950/40 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-200">Mevcut Yedekler</span>
            <span className="text-xs font-mono text-slate-500">
              {list.length} dosya · {totalHuman}
            </span>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            <div className="text-xs font-mono uppercase">Yükleniyor...</div>
          </div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Henüz yedek yok. "Şimdi Yedekle" ile ilk yedeği oluşturun.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
              <tr>
                <th className="text-left px-4 py-2">Dosya</th>
                <th className="text-left px-4 py-2">Tür</th>
                <th className="text-right px-4 py-2">Boyut</th>
                <th className="text-left px-4 py-2">Tarih</th>
                <th className="text-right px-4 py-2">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {list.map((b) => (
                <tr key={b.filename} className="hover:bg-slate-900/40" data-testid={`backup-row-${b.filename}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-200">{b.filename}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm border ${
                      b.kind === "auto"
                        ? "border-emerald-800 text-emerald-400 bg-emerald-950/30"
                        : "border-slate-700 text-slate-300"
                    }`}>
                      {b.kind === "auto" ? "OTOMATİK" : "MANUEL"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-300">{b.size_human}</td>
                  <td className="px-4 py-2 text-xs text-slate-400 font-mono">
                    {new Date(b.created_at).toLocaleString("tr-TR")}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadBackup(b.filename)}
                        data-testid={`backup-download-${b.filename}`}
                        className="h-7 text-[10px] uppercase font-mono border-slate-700 hover:bg-slate-800 rounded-sm"
                        title="İndir"
                      >
                        <Download className="w-3 h-3 mr-1" /> İndir
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmRestore(b)}
                        data-testid={`backup-restore-${b.filename}`}
                        className="h-7 text-[10px] uppercase font-mono border-amber-800 text-amber-400 hover:bg-amber-950/40 rounded-sm"
                        title="Geri yükle"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Yükle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmDelete(b)}
                        data-testid={`backup-delete-${b.filename}`}
                        className="h-7 text-[10px] uppercase font-mono border-rose-900 text-rose-400 hover:bg-rose-950/40 rounded-sm"
                        title="Sil"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Restore Confirmation Dialog */}
      <Dialog open={!!confirmRestore} onOpenChange={(v) => !v && setConfirmRestore(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <ShieldAlert className="w-5 h-5" /> Geri Yükleme Onayı
            </DialogTitle>
            <DialogDescription className="text-slate-300 pt-2 space-y-2">
              <p><b className="text-rose-400">DİKKAT:</b> Bu işlem mevcut tüm verileri (koleksiyonları) silecek ve seçili yedekten geri yükleyecek.</p>
              <p className="text-xs font-mono bg-slate-950 border border-slate-800 rounded-sm p-2">
                {confirmRestore?.filename}<br />
                {confirmRestore?.size_human} · {confirmRestore && new Date(confirmRestore.created_at).toLocaleString("tr-TR")}
              </p>
              <p className="text-xs text-amber-400">
                💡 Önce mevcut veriyi kaybetmemek için "Şimdi Yedekle" ile bir güvenlik yedeği alın.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmRestore(null)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
            >İptal</Button>
            <Button
              onClick={() => doRestore(confirmRestore.filename)}
              disabled={restoring}
              data-testid="confirm-restore-btn"
              className="bg-amber-600 hover:bg-amber-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              Evet, Geri Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400">
              <Trash2 className="w-5 h-5" /> Yedeği Sil
            </DialogTitle>
            <DialogDescription className="text-slate-300 pt-2">
              <p className="text-xs font-mono bg-slate-950 border border-slate-800 rounded-sm p-2">
                {confirmDelete?.filename}<br />
                {confirmDelete?.size_human}
              </p>
              <p className="mt-2 text-sm">Bu yedek dosyası kalıcı olarak silinecek. Devam etmek istiyor musunuz?</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
            >İptal</Button>
            <Button
              onClick={() => doDelete(confirmDelete.filename)}
              data-testid="confirm-delete-btn"
              className="bg-rose-600 hover:bg-rose-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
